import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '../../../../app/hooks';
import Tooltip from '../../../../common/_custom/Tooltip/Tooltip';
import GstDetailsBody from './GstDetailsBody';
import { fetchGstOverviewData } from '../../../../store/dashboard/gstOverview/apiHelper';
import { GstOverviewResponse } from '../../../../store/dashboard/gstOverview/types';
import SignatoriesPill from './SignatoriesPill';
import { useGstOverviewPolling } from './useGstOverviewPolling';
import { TabModule } from '../../../../utils/constants/commonConstants';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import styles from './GstOverview.module.scss';

// FE-hardcoded for now: Revenue Analysis and Expenses Analysis sub-tabs are
// shown as "Coming soon" until those screens ship inside the GST module.
// Remove this constant (and the FALLBACK_GST_SUB_TABS list below) once both
// sub-tabs are live; sub-tab disabled state should then come from polus
// ViewConfig like every other tab.
const DISABLED_SUB_TABS_COMING_SOON = ['revenueAnalysis', 'expensesAnalysis'];
const DEFAULT_PILL_ID = 'gstOverview';

interface SubTabEntry {
    id: string;
    label: string;
}

interface GstDashboardTab {
    id: string;
    subTabs?: SubTabEntry[];
}

interface DashboardConfigShape {
    tabs?: GstDashboardTab[];
}

function GstOverview() {
    const params = useParams();
    const orgId = params.id || '';
    const dashboardConfig = useAppSelector((state) => state.investor.config as DashboardConfigShape | null);

    const subTabs: SubTabEntry[] = useMemo(() => {
        const gstTab = dashboardConfig?.tabs?.find((t) => t.id === TabModule.GST);
        return gstTab?.subTabs ?? [];
    }, [dashboardConfig?.tabs]);

    // First non-disabled sub-tab id from config (falls back to DEFAULT_PILL_ID).
    // Avoids a no-pill-active state if polus ever seeds the GST sub-tabs in a
    // different order than the FE default.
    const initialPillId = useMemo(
        () => subTabs.find((s) => !DISABLED_SUB_TABS_COMING_SOON.includes(s.id))?.id ?? DEFAULT_PILL_ID,
        [subTabs],
    );

    const [activePill, setActivePill] = useState<string>(initialPillId);

    // Resync activePill if subTabs change shape after first render (e.g. config
    // arrives async and the FE-defaulted pill id is not present in the new list).
    useEffect(() => {
        if (!subTabs.some((s) => s.id === activePill)) {
            setActivePill(initialPillId);
        }
    }, [subTabs, activePill, initialPillId]);
    const [data, setData] = useState<GstOverviewResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Single source of truth for fetching. isPollingCall=true suppresses the
    // full loading spinner so polling refetches don't flash the empty state —
    // the user keeps seeing data with a "Refreshing…" pill in the header instead.
    const fetchData = useCallback(
        async (isPollingCall = false) => {
            if (!orgId) return;
            if (!isPollingCall) {
                setLoading(true);
                setError(null);
            }
            try {
                const result = await fetchGstOverviewData(orgId);
                setData(result);
            } catch {
                if (!isPollingCall) setError('Failed to load GST overview');
            } finally {
                if (!isPollingCall) setLoading(false);
            }
        },
        [orgId],
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // BE-driven in-flight signal: polus mergeGstOverview includes the
    // AION_INGEST_GST_PROFILE task-tracker status inline in the response so the
    // FE can react on every fetch (survives browser refresh — no Redux state
    // needed). Non-terminal status ⇒ poll + show loader.
    const taskStatus = data?.AICA_GET_TASK_TRACKER_DATA?.taskTrackerData?.AION_INGEST_GST_PROFILE;
    const isStatusInFlight = !!taskStatus && taskStatus !== 'COMPLETE' && taskStatus !== 'FAILED' && taskStatus !== 'FAILURE';

    // Drawer-driven signal: when the user clicks Unlock/Refresh in the right
    // drawer with Overview selected, RefreshReport cascades 'gst' into
    // modulePollingList via overview.subModules (polus seed). This fires the
    // loader IMMEDIATELY on click — no need to wait for the next fetch cycle
    // to see the BE step transition into TODO/PROCESSING.
    const modulePollingList = useAppSelector((state) => state.investor.modulePollingList);
    const isModulePolling = modulePollingList.includes(TabModule.GST);

    // Either signal keeps the loader visible. They naturally converge: drawer
    // click → modulePollingList fires immediately → BE step writes TODO →
    // response shows non-terminal → both true → BE completes → both false.
    const isInFlight = isStatusInFlight || isModulePolling;

    useGstOverviewPolling(fetchData, isInFlight);

    const lastUpdatedLabel = data?.lastUpdatedAt ? dayjs(data.lastUpdatedAt).format('DD MMM YYYY') : '';

    return (
        <div className={styles.GstPage}>
            {subTabs.length > 0 && (
                <div className={styles.SubTabBar}>
                    <div className={styles.PillBar} role="tablist">
                        {subTabs.map((pill) => {
                            const isDisabled = DISABLED_SUB_TABS_COMING_SOON.includes(pill.id);
                            const isActive = activePill === pill.id;
                            const pillNode = (
                                <button
                                    key={pill.id}
                                    type="button"
                                    role="tab"
                                    aria-selected={isActive}
                                    aria-disabled={isDisabled}
                                    className={clsx(styles.Pill, {
                                        [styles.PillActive]: isActive,
                                        [styles.PillDisabled]: isDisabled,
                                    })}
                                    onClick={isDisabled ? undefined : () => setActivePill(pill.id)}
                                >
                                    {pill.label}
                                </button>
                            );
                            return isDisabled ? (
                                <Tooltip key={pill.id} content="Coming soon" direction="bottom" fitContent usePortal>
                                    {pillNode}
                                </Tooltip>
                            ) : (
                                pillNode
                            );
                        })}
                    </div>
                </div>
            )}
            {activePill === DEFAULT_PILL_ID && (
                <div className={styles.SectionHeader}>
                    <div className={styles.SectionTitle}>GST Overview</div>
                    <div className={styles.HeaderMeta}>
                        {isInFlight && (
                            <span className={styles.RefreshingText} aria-live="polite">
                                Fetching Data...
                                <LoadingSpinner color="var(--primary-text-colour)" height="20px" />
                            </span>
                        )}
                        {lastUpdatedLabel && <span className={styles.LastUpdated}>Last Updated on {lastUpdatedLabel}</span>}
                        {data && <SignatoriesPill signatories={data.summary?.signatories || []} />}
                        <span className={styles.SourcePill}>
                            Source: <span className={styles.SourceValue}>GST</span>
                        </span>
                    </div>
                </div>
            )}
            {activePill === DEFAULT_PILL_ID && <GstDetailsBody loading={loading || (isInFlight && !data)} error={error} data={data} />}
        </div>
    );
}

export default GstOverview;
